/**
 * Journey Object fields (current/cumulative data) use Liferay Rich Text, which is sanitized
 * with AntiSamy (~200KB max per field). Snapshots must not include full BI API payloads,
 * aadhaar photo blobs, or client-only prefetch caches.
 */

function cloneJson(value) {
  if (value == null) return value;
  try {
    return JSON.parse(JSON.stringify(value));
  } catch {
    return null;
  }
}

function slimBiResult(bi) {
  if (bi == null || typeof bi !== "object" || Array.isArray(bi)) return bi;
  const pdf = bi.pdfUrl || bi.PDFURL || bi.pdfURL;
  if (!pdf) return {};
  return { pdfUrl: pdf };
}

/**
 * Reduces Redux journey snapshot size before submitLead / journey PATCH.
 * @param {object|null|undefined} snapshot
 * @returns {object|null|undefined}
 */
export function slimJourneyReduxSnapshotForPersistence(snapshot) {
  const copy = cloneJson(snapshot);
  if (!copy || typeof copy !== "object") {
    return copy;
  }

  if (copy.quoteResult && typeof copy.quoteResult === "object") {
    const qr = { ...copy.quoteResult };
    if (qr.biResult != null) {
      qr.biResult = slimBiResult(qr.biResult);
    }
    // Stage 3 keeps biPayload; Stage 4 holds BI — drop nested duplicates from snapshots.
    delete qr.biPayload;
    delete qr.biResponse;
    delete qr.biPayloadRaw;
    copy.quoteResult = qr;
  }

  delete copy.multiDimensionByPlan;
  delete copy.multiDimensionPremiumResult;

  return copy;
}

const AADHAAR_BLOB_KEYS = new Set([
  "profile_image",
  "raw_xml",
  "zip_data",
  "aadhaar_pdf",
]);

/**
 * Strip large aadhaar KYC blobs before Stage 7 journey persistence.
 * Keeps verification flags, address, and identity fields needed for proposal resume.
 * @param {object|null|undefined} snapshot
 * @returns {object|null|undefined}
 */
export function slimCustomerVerificationSnapshotForPersistence(snapshot) {
  const copy = cloneJson(snapshot);
  if (!copy || typeof copy !== "object") {
    return copy;
  }

  const aadhaar = copy.aadhaar;
  if (aadhaar && typeof aadhaar === "object") {
    const nextAadhaar = { ...aadhaar };
    if (nextAadhaar.response && typeof nextAadhaar.response === "object") {
      const response = { ...nextAadhaar.response };
      for (const key of AADHAAR_BLOB_KEYS) {
        delete response[key];
      }
      nextAadhaar.response = response;
    }
    copy.aadhaar = nextAadhaar;
  }

  return copy;
}
